import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  PlusIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { toast } from "sonner";
import LoadingSpinner from "../components/LoadingSpinner";
import LoanCalculator from "../components/LoanCalculator";
import FAQAccordion from "../components/FAQAccordion";
import EarningFeaturesGrid from "../components/EarningFeaturesGrid";
import { apiService } from "../services/api";
import LenderSelectionModal from "../components/LenderSelectionModal";
import VoltMoneyModal from "../components/VoltMoneyModal";
import { Lender, createUserLoan, triggerSmallcaseApply, scErrorMessage } from "../services/loanService";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [webViewVisible, setWebViewVisible] = useState(false);
  const [finsireLink, setFinsireLink] = useState<string | null>(null);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [hasCreditLineActive, setHasCreditLineActive] = useState(false);
  const [isCreditLineStatusLoading, setIsCreditLineStatusLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [finsirePopupVisible, setFinsirePopupVisible] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [lenderModalVisible, setLenderModalVisible] = useState(false);
  const [voltLink, setVoltLink] = useState<string | null>(null);
  // null = Finsire (existing flow)
  const lenderRef = useRef<Lender | null>(null);

  // PAN modal state
  const [panModalVisible, setPanModalVisible] = useState(false);
  const [panInput, setPanInput] = useState("");
  const [panError, setPanError] = useState("");
  const [isSavingPan, setIsSavingPan] = useState(false);

  const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

  const handlePanInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
    setPanInput(sanitized);
    if (panError) setPanError("");
  };

  const handleSavePan = async () => {
    if (!PAN_REGEX.test(panInput)) {
      setPanError("Invalid PAN format. Expected: ABCDE1234F");
      return;
    }
    setIsSavingPan(true);
    try {
      const response = await apiService.put("/auth/profile/", { pan_number: panInput });
      if (response.data?.success !== false) {
        // Patch local state so subsequent Apply taps don't re-show the modal.
        // Mirrors Android: updateUser({ pan_number: panInput }) before closing.
        updateUser({ pan_number: panInput });
        setPanModalVisible(false);
        setPanInput("");
        setPanError("");
        // Proceed with loan application using the just-saved PAN
        setTimeout(() => handleLoanApplicationWithPan(panInput), 300);
      } else {
        setPanError("Failed to save PAN. Please try again.");
      }
    } catch {
      setPanError("Failed to save PAN. Please try again.");
    } finally {
      setIsSavingPan(false);
    }
  };

  // On mount: refresh credit line status from backend
  useEffect(() => {
    const refreshCreditLine = async () => {
      setIsCreditLineStatusLoading(true);
      try {
        const res = await apiService.post("/auth/refresh-credit-line/", {});
        const data = res.data;
        if (data?.success && data?.credit_line) {
          setHasCreditLineActive(data.credit_line.active ?? false);
          if (
            data.credit_line.withdrawal_link_found &&
            data.credit_line.withdrawal_link
          ) {
            setFinsireLink(data.credit_line.withdrawal_link);
          }
        }
      } catch {
        // silent — button still works, credit line status just defaults to false
      } finally {
        setIsCreditLineStatusLoading(false);
      }
    };
    refreshCreditLine();
  }, []);

  // Get user's location. Returns fresh coords directly to avoid stale-closure issues.
  const getLocation = useCallback(async (): Promise<{
    latitude: number;
    longitude: number;
  } | null> => {
    if (isLocationLoading) return null;

    setIsLocationLoading(true);
    setLocationError(null);

    try {
      if (!navigator.geolocation) {
        throw new Error("Geolocation is not supported by this browser");
      }

      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 300000,
          });
        },
      );

      const coords = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
      setLatitude(coords.latitude);
      setLongitude(coords.longitude);
      setLocationError(null);
      return coords;
    } catch (error: any) {
      let errorMessage = "Unable to retrieve your location. Please try again.";
      if (error.code === 1) {
        errorMessage =
          "Location permission denied. Please allow location access in your browser settings.";
      } else if (error.code === 2) {
        errorMessage =
          "Location is currently unavailable. Please try again later.";
      } else if (error.code === 3) {
        errorMessage = "Location request timed out. Please try again.";
      }
      setLocationError(errorMessage);
      return null;
    } finally {
      setIsLocationLoading(false);
    }
  }, [isLocationLoading]);

  // FAB button event from FloatingTabBar
  const handleLoanApplicationRef = useRef<() => void>(() => {});
  useEffect(() => {
    handleLoanApplicationRef.current = handleLoanApplication;
  });
  useEffect(() => {
    const handler = () => setLenderModalVisible(true);
    window.addEventListener("triggerLoanApplication", handler);
    return () => window.removeEventListener("triggerLoanApplication", handler);
  }, []);

  const submitLenderApplication = async (lender: Lender, lat: number, lng: number, pan: string) => {
    setIsLoading(true);
    try {
      const res = await createUserLoan({
        phone_number: user?.phone_number || "",
        full_name: user?.full_name || "",
        email: user?.email || "",
        platform: lender.platform,
        pan,
        latitude: lat,
        longitude: lng,
      });
      if (res.has_credit_line_active !== undefined) setHasCreditLineActive(res.has_credit_line_active);
      if (lender.partner === "volt") {
        if (!res.apply_credit_link) throw new Error("No application link received");
        setVoltLink(res.apply_credit_link);
        return;
      }
      const token = res.data?.interactionToken;
      if (!token) throw new Error("Service error. Please try again.");
      setIsLoading(false); // SDK renders its own UI
      const status = await triggerSmallcaseApply(token);
      toast.success(status ? `Application status: ${status}` : "Application submitted");
    } catch (e: any) {
      const code = e?.code;
      const msg = scErrorMessage(e);
      if (code === 1012) toast(msg);
      else if (code === 1031) window.open("mailto:support@varicash.com", "_self"); // contact_support, same as sidebar Contact Us
      else toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const submitLoanApplication = async (lat: number, lng: number, pan: string) => {
    if (lenderRef.current) return submitLenderApplication(lenderRef.current, lat, lng, pan);
    setIsLoading(true);
    setIframeError(false);

    try {
      const response = await apiService.post("/finsire/create-user/", {
        name: user?.full_name || "",
        email: user?.email || "",
        phone: user?.phone_number || "",
        pan,
        latitude: lat,
        longitude: lng,
      });

      const responseData = response.data;

      let linkToOpen: string | null = null;

      if (responseData.link && !responseData.data?.link) {
        linkToOpen = responseData.link;
        setHasCreditLineActive(responseData.widthdrawal_link !== null);
      } else {
        if (responseData.has_credit_line_active !== undefined) {
          setHasCreditLineActive(responseData.has_credit_line_active);
        }
        if (responseData.data?.widthdrawal_link) {
          const w = responseData.data.widthdrawal_link;
          linkToOpen = typeof w === "string" ? w : w.bfl_link;
        } else if (responseData.apply_credit_link) {
          linkToOpen = responseData.apply_credit_link;
        } else if (responseData.data?.link) {
          linkToOpen = responseData.data.link;
        }
      }

      if (linkToOpen) {
        try {
          new URL(linkToOpen);
          if (import.meta.env.VITE_OPEN_FINSIRE_LINK_IN_NEW_TAB === "true") {
            window.open(linkToOpen, "_blank", "noopener,noreferrer");
          } else {
            setFinsirePopupVisible(true);
          }
        } catch {
          toast.error("Invalid application link received. Please try again.");
        }
      } else {
        toast.success("Application submitted successfully!");
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        (error.request
          ? "Network error. Please check your connection."
          : "Failed to submit application. Please try again.");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Called after PAN modal saves successfully — skips PAN check
  const handleLoanApplicationWithPan = async (panOverride: string) => {
    let lat = latitude;
    let lng = longitude;
    if (!lat || !lng) {
      const coords = await getLocation();
      if (!coords) {
        toast.error(locationError || "Location required. Please enable location services.");
        return;
      }
      lat = coords.latitude;
      lng = coords.longitude;
    }
    await submitLoanApplication(lat, lng, panOverride);
  };

  const handleLoanApplication = async () => {
    if (isCreditLineStatusLoading) return;

    if (isLocationLoading) {
      toast.error("We're getting your location. Please try again in a moment.");
      return;
    }

    // Resolve coords — use cached state or fetch fresh
    let lat = latitude;
    let lng = longitude;

    if (!lat || !lng) {
      const coords = await getLocation();
      if (!coords) {
        toast.error(
          locationError ||
            "We need your location to process your application. Please enable location services and try again.",
        );
        return;
      }
      lat = coords.latitude;
      lng = coords.longitude;
    }

    // PAN check — show modal to collect PAN (matches Android behaviour)
    if (!user?.pan_number) {
      setPanModalVisible(true);
      return;
    }

    // If credit line already active and link is cached, open directly
    if (!lenderRef.current && hasCreditLineActive && finsireLink) {
      setWebViewVisible(true);
      return;
    }

    await submitLoanApplication(lat, lng, user.pan_number);
  };

  const closeWebView = () => {
    setWebViewVisible(false);
    setFinsireLink(null);
    setIframeError(false);
    setIframeLoading(true);
    setShowCloseConfirm(false);
  };

  // Dispatch custom event so FloatingTabBar can hide/show itself
  // Also reset iframe loading state each time WebView opens
  useEffect(() => {
    if (webViewVisible) {
      setIframeLoading(true);
      window.dispatchEvent(new CustomEvent("webViewOpen"));
    } else {
      window.dispatchEvent(new CustomEvent("webViewClose"));
    }
  }, [webViewVisible]);

  const handleIframeLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
    setIframeLoading(false);
    const iframe = e.target as HTMLIFrameElement;
    try {
      const iframeUrl = iframe.contentWindow?.location.href;
      if (iframeUrl?.includes("session-expired")) {
        setIframeError(true);
        if (retryCount < 2) {
          toast.error("Session expired. Generating new link...");
          setTimeout(() => {
            closeWebView();
            setRetryCount((prev) => prev + 1);
            handleLoanApplication();
          }, 2000);
        } else {
          toast.error("Session expired. Please try again later.");
          setRetryCount(0);
        }
      }
    } catch {
      // Expected: cross-origin restriction — iframe loaded normally
    }
  };

  const handleIframeError = () => {
    setIframeLoading(false);
    setIframeError(true);
    toast.error("Failed to load application. Please try again.");
  };

  // Button label — matches Android priority order
  const buttonLabel = () => {
    if (isCreditLineStatusLoading) return "Checking credit status...";
    if (isLoading) return "Processing...";
    if (isLocationLoading) return "Getting Location...";
    if (hasCreditLineActive) return "Withdraw from Credit Line";
    return "Apply for Credit Line";
  };

  return (
    <div className="min-h-screen bg-black max-w-[400px] mx-auto">
      <div
        className="min-h-screen"
        style={{
          background: "linear-gradient(to top right, #000000 0%, #323435 100%)",
        }}
      >
        <div className="w-full max-w-[400px] mx-auto">
          <div className="overflow-y-auto">
            {/* Header Section — matches Android headerSection: bg #151718, overflow hidden */}
            <div
              className="relative overflow-hidden px-4 pt-4"
              style={{ backgroundColor: "#151718", paddingBottom: "30px" }}
            >
              {/* Decorative rotated rectangles — matches Android decorative1 / decorative2 */}
              <div
                className="absolute"
                style={{
                  width: "106px",
                  height: "243px",
                  left: "-94px",
                  top: "-28px",
                  backgroundColor: "#333333",
                  opacity: 0.9,
                  transform: "rotate(-28.08deg)",
                  zIndex: 1,
                }}
              />
              <div
                className="absolute"
                style={{
                  width: "217px",
                  height: "243px",
                  right: "-50px",
                  top: "-80px",
                  backgroundColor: "#333333",
                  opacity: 0.9,
                  transform: "rotate(-28.08deg)",
                  zIndex: 1,
                }}
              />

              {/* User Row */}
              <div
                className="flex items-center justify-between relative"
                style={{ minHeight: "56px", zIndex: 4 }}
              >
                {/* Avatar + name → navigate to profile */}
                <button
                  onClick={() => navigate("/profile")}
                  className="flex items-center text-left"
                  style={{ gap: "14px" }}
                >
                  <div
                    className="rounded-full overflow-hidden shadow-lg flex items-center justify-center shrink-0"
                    style={{
                      width: "48px",
                      height: "48px",
                      backgroundColor: "#222",
                    }}
                  >
                    <img
                      src="/assets/images/avatar.png"
                      alt="User Avatar"
                      className="w-full h-full rounded-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        const parent = target.parentElement!;
                        parent.innerHTML = `<span style="color:white;font-weight:600;font-size:14px">${
                          user?.full_name?.charAt(0) || "U"
                        }</span>`;
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, justifyContent: "center", minHeight: "48px" }}>
                    <p style={{ fontSize: "11px", color: "#787878", marginBottom: "1px", marginLeft: "2px" }}>
                      Hello, Welcome back!
                    </p>
                    <p style={{ fontSize: "16px", color: "#fff", fontWeight: "500", marginLeft: "2px" }}>
                      {user?.full_name
                        ?.split(" ")[0]
                        ?.toLowerCase()
                        .replace(/^./, (c) => c.toUpperCase()) || "User"}
                    </p>
                  </div>
                </button>

                {/* Header Icons */}
                <div className="flex items-center" style={{ gap: "8px", marginLeft: "10px" }}>
                  <button
                    onClick={() => window.open("tel:8197698484", "_self")}
                    className="p-1 flex items-center justify-center"
                  >
                    <PhoneIcon className="w-5 h-5 text-white" />
                  </button>
                  <button
                    onClick={() => window.open("mailto:contact@varicash.com", "_self")}
                    className="p-1 flex items-center justify-center"
                  >
                    <EnvelopeIcon className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>

              {/* Main Card — matches Android mainCard gradient #0A0C0E → #333536 */}
              <div
                className="relative overflow-hidden shadow-2xl"
                style={{
                  background: "linear-gradient(to top right, #0A0C0E 0%, #333536 100%)",
                  marginTop: "40px",
                  marginBottom: "10px",
                  borderRadius: "30px",
                  padding: "2px",
                  zIndex: 2,
                }}
              >
                <div
                  className="relative w-full"
                  style={{
                    paddingTop: "28px",
                    paddingBottom: "20px",
                    paddingLeft: "15px",
                    paddingRight: "0px",
                    zIndex: 2,
                  }}
                >
                  <p style={{ fontSize: "14px", color: "#787878", marginBottom: "2px", marginLeft: "2px" }}>
                    The
                  </p>
                  <h2
                    style={{
                      fontSize: "40px",
                      color: "#fff",
                      fontWeight: "500",
                      lineHeight: "50px",
                      marginBottom: "2px",
                      marginLeft: "2px",
                    }}
                  >
                    Smart Credit
                  </h2>
                  <p style={{ fontSize: "14px", color: "#787878", marginBottom: "2px", marginLeft: "2px" }}>
                    Against{" "}
                    <span style={{ color: "#fff", fontWeight: "bold" }}>Mutual Funds</span>
                  </p>

                  {/* Action Button — matches Android actionButtonsRow */}
                  <div
                    className="flex"
                    style={{ marginTop: "12px", marginRight: "15px", alignSelf: "stretch" }}
                  >
                    <button
                      onClick={() => setLenderModalVisible(true)}
                      disabled={isLocationLoading || isLoading || isCreditLineStatusLoading}
                      className="flex-1 flex items-center justify-center transition-all duration-300 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: "#F7F7F7",
                        borderRadius: "50px",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        paddingLeft: "10px",
                        paddingRight: "10px",
                        opacity: isLocationLoading || isCreditLineStatusLoading ? 0.7 : 1,
                      }}
                    >
                      {isLoading || isCreditLineStatusLoading ? (
                        <LoadingSpinner size="sm" className="mr-2" />
                      ) : (
                        <PlusIcon className="w-6 h-6 mr-1" style={{ color: "black" }} />
                      )}
                      <span
                        style={{
                          color: "black",
                          fontSize: "14px",
                          fontWeight: "600",
                          textAlign: "center",
                        }}
                      >
                        {buttonLabel()}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Earning Features Grid — directly after headerSection, no extra wrapper */}
            <EarningFeaturesGrid />

            {/* Content Sections — matches Android: bg white, paddingTop 20 */}
            <div style={{ backgroundColor: "#ffffff", paddingTop: "20px" }}>
              <h2
                style={{
                  fontSize: "18px",
                  fontWeight: "800",
                  color: "#2c2c2c",
                  marginBottom: "20px",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  fontFamily: "DM Sans, sans-serif",
                }}
              >
                How it Works?
              </h2>
              <LoanCalculator />
              <FAQAccordion />
            </div>
            {/* Bottom spacer — matches Android height: 100 */}
            <div style={{ height: "100px", backgroundColor: "#fff" }} />
          </div>
        </div>
      </div>

      {/* WebView Modal — matches Android WebView modal structure */}
      {webViewVisible && finsireLink && (
        <div className="fixed inset-0 z-50 flex justify-center bg-white">
          <div className="w-full max-w-[400px] flex flex-col bg-white h-full">

            {/* Header — matches Android webviewHeader */}
            <div
              className="flex items-center bg-white"
              style={{
                padding: "8px",
                borderBottom: "0.5px solid #e0e0e0",
                gap: "4px",
              }}
            >
              {/* Back / close button — matches Android backButton */}
              <button
                onClick={() => setShowCloseConfirm(true)}
                style={{
                  padding: "10px 20px 10px 10px",
                  borderRadius: "20px",
                  background: "transparent",
                  border: "none",
                  minWidth: "40px",
                  minHeight: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <ArrowLeftIcon className="w-6 h-6" style={{ color: "#000" }} />
              </button>

              {/* Title — matches Android webviewTitle */}
              <h2
                style={{
                  flex: 1,
                  fontSize: "18px",
                  fontWeight: "500",
                  color: "#000",
                  margin: 0,
                  textAlign: "left",
                  fontFamily: "DM Sans, sans-serif",
                }}
              >
                {hasCreditLineActive ? "Your Credit Line" : "Credit Line Application"}
              </h2>

              {/* Home button — matches Android homeButton */}
              <button
                onClick={() => setShowCloseConfirm(true)}
                style={{
                  padding: "10px 10px 10px 20px",
                  borderRadius: "20px",
                  background: "transparent",
                  border: "none",
                  minWidth: "40px",
                  minHeight: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                {/* Home icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  width="24"
                  height="24"
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              </button>
            </div>

            {/* Download app banner — shown above iframe */}
            <div
              style={{
                backgroundColor: "#FEF3C7",
                borderBottom: "1px solid #FDE68A",
                padding: "8px 16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexShrink: 0,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#D97706"/>
              </svg>
              <p style={{ fontSize: "11px", color: "#92400E", margin: 0, lineHeight: "15px" }}>
                If you face any error,{" "}
                <a
                  href="https://varicash.in/download"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#07786B", fontWeight: "600", textDecoration: "underline" }}
                >
                  download our Android/iOS app
                </a>
                {" "}and continue from there.
              </p>
            </div>

            {/* Iframe container */}
            <div className="flex-1 relative">
              <iframe
                ref={iframeRef}
                src={finsireLink}
                className="w-full h-full border-0"
                title="Credit Line Application"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation allow-downloads allow-storage-access-by-user-activation allow-popups-to-escape-sandbox"
                allow="geolocation; camera; microphone; storage-access *"
                referrerPolicy="no-referrer-when-downgrade"
                onLoad={handleIframeLoad}
                onError={handleIframeError}
              />

              {/* Loading overlay — matches Android renderLoading */}
              {iframeLoading && !iframeError && (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center bg-white"
                  style={{ zIndex: 10 }}
                >
                  <LoadingSpinner size="lg" />
                  <p className="mt-3 text-base text-gray-500" style={{ fontFamily: "DM Sans, sans-serif" }}>
                    {hasCreditLineActive ? "Opening Your Dashboard..." : "Loading application..."}
                  </p>
                </div>
              )}

              {/* Error overlay */}
              {iframeError && (
                <div className="absolute inset-0 bg-white flex items-center justify-center z-10">
                  <div className="text-center p-6 max-w-sm">
                    <div className="text-6xl mb-4">⚠️</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Session Error</h3>
                    <p className="text-gray-600 mb-6">
                      {retryCount >= 2
                        ? "Unable to establish session. Please try again later."
                        : "The application session encountered an error."}
                    </p>
                    <div className="flex gap-3 justify-center">
                      <button
                        onClick={() => { setIframeError(false); closeWebView(); setRetryCount(0); }}
                        className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                      >
                        Close
                      </button>
                      {retryCount < 2 && (
                        <button
                          onClick={() => {
                            setIframeError(false);
                            closeWebView();
                            setRetryCount((prev) => prev + 1);
                            setTimeout(() => handleLoanApplication(), 500);
                          }}
                          className="bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                        >
                          Retry
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Close Confirmation Dialog — z-[60] so it renders above the iframe (z-50) */}
      {showCloseConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Go to Dashboard
            </h3>
            <p className="text-gray-600 mb-6">
              Do you really want to go to Dashboard?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCloseConfirm(false)}
                className="flex-1 py-3 px-4 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={closeWebView}
                className="flex-1 py-3 px-4 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PAN Input Modal — shown when user has no PAN and tries to apply */}
      {panModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[60] flex items-end justify-center">
          <div className="bg-white w-full max-w-[400px] rounded-t-2xl p-6 pb-10 shadow-2xl mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Enter PAN Number
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              Your PAN is required to apply for a credit line.
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-2">
              PAN Number
            </label>
            <input
              type="text"
              value={panInput}
              onChange={handlePanInputChange}
              placeholder="ABCDE1234F"
              maxLength={10}
              className={`w-full px-4 py-4 text-base border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-400 mb-1 ${
                panError ? "border-red-400 bg-red-50" : "border-gray-300"
              }`}
              autoFocus
            />
            {panError && (
              <p className="text-xs text-red-600 mb-4">{panError}</p>
            )}
            {!panError && <div className="mb-4" />}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setPanModalVisible(false);
                  setPanInput("");
                  setPanError("");
                }}
                disabled={isSavingPan}
                className="flex-1 py-3 px-4 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePan}
                disabled={isSavingPan || panInput.length !== 10}
                className="flex-1 py-3 px-4 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSavingPan ? (
                  <LoadingSpinner size="sm" className="mr-2" />
                ) : null}
                {isSavingPan ? "Saving..." : "Save & Continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      {lenderModalVisible && (
        <LenderSelectionModal
          onClose={() => setLenderModalVisible(false)}
          onSelect={async (l) => {
            lenderRef.current = l.partner === "finsire" ? null : l;
            await handleLoanApplication();
            setLenderModalVisible(false);
          }}
        />
      )}
      {voltLink && <VoltMoneyModal link={voltLink} phone={user?.phone_number} onClose={() => setVoltLink(null)} />}

      {/* Finsire App Download Popup */}
      {finsirePopupVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[60] flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl text-center">
            <div className="flex items-center justify-center mb-4">
              <div style={{ width: 56, height: 56, borderRadius: 28, backgroundColor: "#ECFDF5", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z" fill="#07786B"/>
                </svg>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Download Our App to Continue
            </h3>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              To continue with the credit line application, please download our Android or iOS app.
            </p>
            <a
              href="https://varicash.in/download"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-3 px-4 rounded-lg text-white font-medium mb-3 text-center"
              style={{ backgroundColor: "#07786B" }}
            >
              Download App
            </a>
            <button
              onClick={() => setFinsirePopupVisible(false)}
              className="w-full py-3 px-4 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center space-y-4">
            <LoadingSpinner size="lg" />
            <p className="text-gray-700 font-medium">
              Processing your application...
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
